import { useState, useEffect } from "react";
import { useAppServices } from "../../../hook/services";
import { useNavigate } from "react-router-dom";

const Category = () => {
  const navigate = useNavigate();
  const AppService = useAppServices();
  const [processing, setProcessing] = useState(true);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const getData = async () => {
    const { response } = await AppService.knowledgebase_category.get();
    if (response) {
      setCategories(response.data);
    } else {
      setCategories([]);
    }
    setProcessing(false);
  };

  useEffect(() => {
    getData();
  }, []);

  const handleView = async (id) => {
    navigate(`./${id}`);
  };

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredCategories = !normalizedQuery
    ? categories
    : categories.filter((c) =>
        (c.name || "").toLowerCase().includes(normalizedQuery)
      );

  return (
    <div className="kb-page">
      {/* Hero */}
      <header className="kb-hero">
        <div className="container kb-hero-inner text-center">
          <span className="kb-hero-icon d-inline-flex align-items-center justify-content-center mb-3">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
            </svg>
          </span>
          <h1 className="kb-hero-title">Knowledgebase</h1>
          <p className="kb-hero-subtitle mb-4">
            Browse help articles by category, or search for what you need below.
          </p>

          <div className="kb-hero-search-wrap mx-auto">
            <span className="kb-hero-search-icon" aria-hidden>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </span>
            <input
              type="text"
              className="kb-hero-search"
              placeholder="Search categories…"
              aria-label="Search categories"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                type="button"
                className="kb-hero-search-clear"
                onClick={() => setSearchQuery("")}
                aria-label="Clear search"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Section */}
      <div className="container kb-section">
        <div className="kb-section-head d-flex align-items-end justify-content-between mb-3 flex-wrap gap-2">
          <h3 className="kb-section-title mb-0">
            {normalizedQuery ? "Search results" : "Browse topics"}
          </h3>
          {!processing && (
            <span className="kb-section-meta">
              {filteredCategories.length}{" "}
              {filteredCategories.length === 1 ? "category" : "categories"}
            </span>
          )}
        </div>

        {processing ? (
          <div className="row g-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div className="col-md-6 col-lg-4" key={i}>
                <div className="kb-card-skel">
                  <div className="d-flex align-items-center gap-3 mb-3">
                    <div className="kb-skel rounded" style={{ width: 36, height: 36 }} />
                    <div className="flex-grow-1">
                      <div className="kb-skel rounded" style={{ height: 12, width: "65%" }} />
                      <div className="kb-skel rounded mt-2" style={{ height: 10, width: "40%" }} />
                    </div>
                  </div>
                  <div className="kb-skel rounded" style={{ height: 32, width: "100%" }} />
                </div>
              </div>
            ))}
          </div>
        ) : filteredCategories.length === 0 ? (
          <div className="kb-empty d-flex flex-column align-items-center text-center">
            <div className="kb-empty-icon d-flex align-items-center justify-content-center mb-3">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </div>
            <h5 className="kb-empty-title">
              {normalizedQuery ? "No categories found" : "No categories yet"}
            </h5>
            <p className="kb-empty-text mb-0">
              {normalizedQuery
                ? `No categories match "${searchQuery}". Try a different search term.`
                : "Categories will appear here once they're created."}
            </p>
          </div>
        ) : (
          <div className="row g-3">
            {filteredCategories.map((cat) => (
              <div className="col-md-6 col-lg-4" key={cat._id}>
                <div
                  className="kb-card h-100 d-flex flex-column"
                  role="button"
                  tabIndex={0}
                  onClick={() => handleView(cat._id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleView(cat._id);
                    }
                  }}
                >
                  <span
                    className="kb-card-accent"
                    aria-hidden="true"
                  />
                  <div className="kb-card-header d-flex align-items-center gap-3">
                    <span className="kb-card-icon d-inline-flex align-items-center justify-content-center">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                      </svg>
                    </span>
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      <h6 className="kb-card-title mb-0 text-truncate">
                        {cat.name || "Untitled category"}
                      </h6>
                    </div>
                    <span className="kb-card-chevron" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <KbStyles />
    </div>
  );
};

const KbStyles = () => (
  <style>{`
    .kb-page {
      font-family: 'Inter', sans-serif;
      background: #fafafa;
      min-height: 100vh;
    }

    /* Hero */
    .kb-hero {
      padding: 48px 16px 56px;
      background: linear-gradient(180deg, #eef2ff 0%, #f5f3ff 60%, #fafafa 100%);
      border-bottom: 1px solid #ececec;
    }
    .kb-hero-inner { max-width: 720px; margin: 0 auto; }
    .kb-hero-icon {
      width: 64px;
      height: 64px;
      border-radius: 18px;
      background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
      color: #4f46e5;
      border: 1px solid #e0e7ff;
      box-shadow: 0 8px 24px rgba(79, 70, 229, 0.12);
    }
    .kb-hero-title {
      font-size: 1.875rem;
      font-weight: 700;
      letter-spacing: -0.015em;
      color: #0f172a;
      margin: 0 0 8px;
    }
    .kb-hero-subtitle {
      font-size: 0.9375rem;
      color: #475569;
      line-height: 1.55;
    }
    .kb-hero-search-wrap {
      position: relative;
      max-width: 560px;
      background: #fff;
      border-radius: 12px;
      box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06), 0 2px 4px rgba(15, 23, 42, 0.04);
      border: 1px solid #ececec;
    }
    .kb-hero-search-icon {
      position: absolute;
      left: 16px;
      top: 50%;
      transform: translateY(-50%);
      color: #9ca3af;
      pointer-events: none;
      display: inline-flex;
    }
    .kb-hero-search {
      width: 100%;
      height: 48px;
      border: 0;
      border-radius: 12px;
      padding: 0 48px 0 44px;
      background: transparent;
      color: #1f2937;
      font-size: 0.9375rem;
      outline: none;
      font-family: inherit;
    }
    .kb-hero-search::placeholder { color: #9ca3af; }
    .kb-hero-search-clear {
      position: absolute;
      right: 8px;
      top: 50%;
      transform: translateY(-50%);
      width: 30px;
      height: 30px;
      border: 0;
      background: transparent;
      color: #6b7280;
      border-radius: 8px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: background 0.15s ease, color 0.15s ease;
    }
    .kb-hero-search-clear:hover { background: #f3f4f6; color: #1f2937; }

    /* Section */
    .kb-section { padding: 32px 16px 64px; }
    .kb-section-title {
      font-size: 1.0625rem;
      font-weight: 600;
      color: #1f2937;
      letter-spacing: -0.005em;
    }
    .kb-section-meta {
      font-size: 0.75rem;
      font-weight: 500;
      color: #6b7280;
    }

    /* Card */
    .kb-card {
      position: relative;
      background: #fff;
      border: 1px solid #ececec;
      border-radius: 12px;
      padding: 16px 18px;
      cursor: pointer;
      overflow: hidden;
      transition: border-color 0.15s ease, box-shadow 0.2s ease, transform 0.15s ease;
    }
    .kb-card:hover {
      border-color: #c7d2fe;
      box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04), 0 8px 20px rgba(79, 70, 229, 0.08);
      transform: translateY(-1px);
    }
    .kb-card:focus-visible {
      outline: none;
      border-color: #4f46e5;
      box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.18);
    }
    .kb-card-accent {
      position: absolute;
      inset-inline: 0;
      top: 0;
      height: 2px;
      background: linear-gradient(90deg, #6366f1, #8b5cf6, #ec4899);
      opacity: 0;
      transition: opacity 0.2s ease;
    }
    .kb-card:hover .kb-card-accent { opacity: 1; }
    .kb-card-icon {
      width: 36px;
      height: 36px;
      border-radius: 9px;
      background: #eef2ff;
      color: #4f46e5;
      flex-shrink: 0;
      transition: background 0.15s ease;
    }
    .kb-card:hover .kb-card-icon { background: #e0e7ff; }
    .kb-card-title {
      font-size: 0.9375rem;
      font-weight: 600;
      color: #1f2937;
      letter-spacing: -0.005em;
    }
    .kb-card-chevron {
      color: #cbd5e1;
      transition: color 0.15s ease, transform 0.15s ease;
    }
    .kb-card:hover .kb-card-chevron {
      color: #4f46e5;
      transform: translateX(2px);
    }

    /* Empty */
    .kb-empty {
      border: 1px dashed #e5e5e5;
      border-radius: 12px;
      background: #fff;
      padding: 56px 24px;
    }
    .kb-empty-icon {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      background: #fafafa;
      border: 1px solid #ececec;
      color: #9ca3af;
    }
    .kb-empty-title {
      font-size: 0.9375rem;
      font-weight: 600;
      color: #1f2937;
      margin: 0 0 4px;
    }
    .kb-empty-text {
      font-size: 0.8125rem;
      color: #6b7280;
    }

    /* Skeleton */
    .kb-card-skel {
      background: #fff;
      border: 1px solid #ececec;
      border-radius: 12px;
      padding: 16px 18px;
    }
    .kb-skel {
      background: linear-gradient(90deg, #eef2f7 0%, #f3f4f6 50%, #eef2f7 100%);
      background-size: 200% 100%;
      animation: kb-shimmer 1.4s ease-in-out infinite;
    }
    @keyframes kb-shimmer {
      0% { background-position: 100% 0; }
      100% { background-position: -100% 0; }
    }
  `}</style>
);

export default Category;
